import { useEffect, useState } from 'react';
import api from '../services/api';
import Button from '../components/Button';
import Input from '../components/Input';
import Select from '../components/Select';
import LookupSelect, { LookupOption } from '../components/LookupSelect';
import Table, { Column } from '../components/Table';
import ConfirmDialog from '../components/ConfirmDialog';

interface Employee {
  id: string;
  code: string;
  email: string;
  full_name: string;
  employment_type: string;
  start_date: string;
  resignation_date?: string;
  is_active: boolean;
}

const FUND_CODES = [
  { code: 'SDF', label: 'SDF — Skills Development Fund' },
  { code: 'MBMF', label: 'MBMF — Mosque Building & Mendaki Fund' },
  { code: 'SINDA', label: 'SINDA Fund' },
  { code: 'CDAC', label: 'CDAC Fund' },
  { code: 'ECF', label: 'ECF — Eurasian Community Fund' },
  { code: 'CC', label: 'CC — Community Chest (SHARE)' },
];

const EMPTY_FORM: Record<string, any> = {
  // General
  code: '',
  email: '',
  firstName: '',
  lastName: '',
  mobileNumber: '',
  dateOfBirth: '',
  gender: '',
  nationalityId: '',
  nric: '',
  passportNumber: '',
  visaNumber: '',
  idType: '',
  raceId: '',
  religionId: '',
  maritalStatus: '',
  photoUrl: '',
  address: '',
  country: '',
  departmentId: '',
  designationId: '',
  sectionId: '',
  categoryId: '',
  costCenter: '',
  managerId: '',
  startDate: '',
  probationEndDate: '',
  confirmationDate: '',
  employmentType: 'Permanent',
  citizenshipStatus: '',
  prStartDate: '',
  workingHoursPerYear: '',
  workingDaysPerWeek: '',
  workingHoursPerDay: '',
  // E-PAYROLL Basic 1
  basicSalary: '',
  isVip: false,
  otRateCapped2600: false,
  payType: 'ONCE_A_MONTH',
  midMonthType: '',
  midMonthPercentage: '',
  midMonthFixedAmount: '',
  cpfContributionType: '',
  fwlContributionType: '',
  prApprovalDate: '',
  cpfOption: '',
  payDayGroupId: '',
  // E-PAYROLL Basic 2
  employerCpfRefNo: '',
  salaryPayMode: '',
  bankId: '',
  bankAccountNo: '',
  // E-PAYROLL Additional 1
  epWpNumber: '',
  epWpExpiryDate: '',
  // E-TMS
  otType: 'NORMAL_RATE',
  otFlatRateAmount: '',
  mainBranchId: '',
  defaultShiftId: '',
  shiftAssignmentMethod: 'SCHEDULE',
  dutyRosterId: '',
  dutyRosterGroup: '',
  autoShiftId: '',
  // E-LEAVE
  leaveCategoryId: '',
  excludeDaysGroupId: '',
  replacementStaffId: '',
  // E-CLAIM
  claimDateRangeId: '',
  claimLimitId: '',
  // Fund opt-outs
  fundOptOuts: [] as string[],
};

const fetchDepartmentOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get(`/masters/departments?search=${encodeURIComponent(search)}`);
  return res.data.departments.map((d: any) => ({ value: d.id, label: `${d.name} (${d.code})` }));
};
const fetchDesignationOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get(`/masters/designations?search=${encodeURIComponent(search)}`);
  return res.data.designations.map((d: any) => ({ value: d.id, label: `${d.name} (${d.code})` }));
};
const fetchBranchOptions = async (): Promise<LookupOption[]> => {
  const res = await api.get('/tms/branches');
  return (res.data.branches || []).map((b: any) => ({ value: b.id, label: `${b.name} (${b.code})` }));
};
const fetchShiftOptions = async (): Promise<LookupOption[]> => {
  const res = await api.get('/tms/shifts');
  return (res.data.shifts || []).map((s: any) => ({ value: s.id, label: `${s.name} (${s.code})` }));
};
const fetchDutyRosterOptions = async (): Promise<LookupOption[]> => {
  const res = await api.get('/tms/duty-rosters');
  return (res.data.rosters || []).map((r: any) => ({ value: r.id, label: `${r.name} (${r.code})` }));
};
const fetchEmployeeOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get(`/employees?search=${encodeURIComponent(search)}`);
  return (res.data.employees || []).map((e: any) => ({ value: e.id, label: `${e.full_name} (${e.code})` }));
};
const fetchLeaveCategoryOptions = async (): Promise<LookupOption[]> => {
  const res = await api.get('/lms/masters/leave-categories');
  return (res.data.leaveCategories || []).map((c: any) => ({ value: c.id, label: `${c.name} (${c.code})` }));
};
const fetchExcludeDaysOptions = async (): Promise<LookupOption[]> => {
  const res = await api.get('/lms/masters/exclude-days');
  return (res.data.excludeDayss || []).map((e: any) => ({ value: e.id, label: `${e.name} (${e.code})` }));
};

const TABS = [
  'General',
  'E-PAYROLL Basic 1',
  'E-PAYROLL Basic 2',
  'E-PAYROLL Additional 1',
  'E-PAYROLL Additional 2',
  'E-TMS',
  'E-LEAVE',
  'E-CLAIM',
  'E-APPRAISAL',
] as const;
type Tab = (typeof TABS)[number];

export default function Employees() {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResigned, setShowResigned] = useState(false);
  const [mode, setMode] = useState<'list' | 'form'>('list');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('General');
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [lookupLabels, setLookupLabels] = useState<Record<string, string>>({});
  const [resignTarget, setResignTarget] = useState<Employee | null>(null);
  const [unmappedUsers, setUnmappedUsers] = useState<Array<{ id: string; email: string; name: string }>>([]);
  const [newEmployeeId, setNewEmployeeId] = useState<string | null>(null);
  const [savedEmployeeId, setSavedEmployeeId] = useState<string | null>(null);

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showResigned]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/employees?showResigned=${showResigned}`);
      setEmployees(response.data.employees || []);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const setField = (key: string, value: any) => setFormData((prev) => ({ ...prev, [key]: value }));

  const openCreateForm = () => {
    setEditingId(null);
    setSavedEmployeeId(null);
    setFormData(EMPTY_FORM);
    setLookupLabels({});
    setTab('General');
    setMode('form');
  };

  const openEditForm = async (emp: Employee) => {
    try {
      const res = await api.get(`/employees/${emp.id}`);
      const full = res.data.employee;
      const toDate = (v: string) => (v ? v.slice(0, 10) : '');
      setEditingId(emp.id);
      setSavedEmployeeId(emp.id);
      setFormData({
        ...EMPTY_FORM,
        code: full.code || '',
        email: full.email || '',
        firstName: full.first_name || '',
        lastName: full.last_name || '',
        mobileNumber: full.mobile_number || '',
        dateOfBirth: toDate(full.date_of_birth),
        gender: full.gender || '',
        nationalityId: full.nationality_id || '',
        nric: full.nric || '',
        passportNumber: full.passport_number || '',
        visaNumber: full.visa_number || '',
        idType: full.id_type || '',
        raceId: full.race_id || '',
        religionId: full.religion_id || '',
        maritalStatus: full.marital_status || '',
        photoUrl: full.photo_url || '',
        address: full.address || '',
        country: full.country || '',
        departmentId: full.department_id || '',
        designationId: full.designation_id || '',
        sectionId: full.section_id || '',
        categoryId: full.category_id || '',
        costCenter: full.cost_center || '',
        managerId: full.manager_id || '',
        startDate: toDate(full.start_date),
        probationEndDate: toDate(full.probation_end_date),
        confirmationDate: toDate(full.confirmation_date),
        employmentType: full.employment_type || 'Permanent',
        citizenshipStatus: full.citizenship_status || '',
        prStartDate: toDate(full.pr_start_date),
        workingHoursPerYear: full.working_hours_per_year ?? '',
        workingDaysPerWeek: full.working_days_per_week ?? '',
        workingHoursPerDay: full.working_hours_per_day ?? '',
        basicSalary: full.basic_salary ?? '',
        isVip: !!full.is_vip,
        otRateCapped2600: !!full.ot_rate_capped_2600,
        payType: full.pay_type || 'ONCE_A_MONTH',
        midMonthType: full.mid_month_type || '',
        midMonthPercentage: full.mid_month_percentage ?? '',
        midMonthFixedAmount: full.mid_month_fixed_amount ?? '',
        cpfContributionType: full.cpf_contribution_type || '',
        fwlContributionType: full.fwl_contribution_type || '',
        prApprovalDate: toDate(full.pr_approval_date),
        cpfOption: full.cpf_option || '',
        payDayGroupId: full.pay_day_group_id || '',
        employerCpfRefNo: full.employer_cpf_ref_no || '',
        salaryPayMode: full.salary_pay_mode || '',
        bankId: full.bank_id || '',
        bankAccountNo: full.bank_account_no || '',
        epWpNumber: full.ep_wp_number || '',
        epWpExpiryDate: toDate(full.ep_wp_expiry_date),
        otType: full.ot_type || 'NORMAL_RATE',
        otFlatRateAmount: full.ot_flat_rate_amount ?? '',
        mainBranchId: full.main_branch_id || '',
        defaultShiftId: full.default_shift_id || '',
        shiftAssignmentMethod: full.shift_assignment_method || 'SCHEDULE',
        dutyRosterId: full.duty_roster_id || '',
        dutyRosterGroup: full.duty_roster_group || '',
        autoShiftId: full.auto_shift_id || '',
        leaveCategoryId: full.leave_category_id || '',
        excludeDaysGroupId: full.exclude_days_group_id || '',
        replacementStaffId: full.replacement_staff_id || '',
        claimDateRangeId: full.claim_date_range_id || '',
        claimLimitId: full.claim_limit_id || '',
        fundOptOuts: res.data.fundOptOuts || [],
      });
      setLookupLabels({});
      setTab('General');
      setMode('form');
    } catch (error) {
      alert('Failed to load employee details');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (!payload.code) delete payload.code; // let auto-generation kick in if configured
      if (editingId) {
        await api.put(`/employees/${editingId}`, payload);
        setMode('list');
        fetchEmployees();
      } else {
        const res = await api.post('/employees', payload);
        setSavedEmployeeId(res.data.employeeId);
        setEditingId(res.data.employeeId);
        setFormData((prev) => ({ ...prev, code: res.data.code }));
        if (res.data.unmappedUsers?.length > 0) {
          setUnmappedUsers(res.data.unmappedUsers);
          setNewEmployeeId(res.data.employeeId);
        } else {
          setMode('list');
          fetchEmployees();
        }
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to save employee');
    }
  };

  const handleResign = async () => {
    if (!resignTarget) return;
    try {
      await api.delete(`/employees/${resignTarget.id}`, {
        data: { resignationDate: new Date().toISOString().slice(0, 10), resignationReason: '' },
      });
      setResignTarget(null);
      fetchEmployees();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to resign employee');
    }
  };

  const mapUnmappedUser = async (userId: string) => {
    if (!newEmployeeId) return;
    try {
      await api.put(`/admin/users/${userId}`, { employeeId: newEmployeeId });
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to map user');
    } finally {
      setUnmappedUsers([]);
      setNewEmployeeId(null);
      setMode('list');
      fetchEmployees();
    }
  };

  const columns: Column<Employee>[] = [
    { key: 'code', header: 'Code', sortable: true },
    { key: 'full_name', header: 'Name', sortable: true },
    { key: 'email', header: 'Email' },
    { key: 'employment_type', header: 'Employment Type' },
    {
      key: 'start_date',
      header: 'Start Date',
      render: (row) => (row.start_date ? new Date(row.start_date).toLocaleDateString() : '—'),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (row.is_active ? <span className="text-green-600">Active</span> : <span className="text-red-500">Resigned</span>),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <div className="flex gap-2">
          <button onClick={() => openEditForm(row)} className="text-blue-600 hover:underline text-sm">
            Profile
          </button>
          {row.is_active && (
            <button onClick={() => setResignTarget(row)} className="text-red-600 hover:underline text-sm">
              Resign
            </button>
          )}
        </div>
      ),
    },
  ];

  if (mode === 'form') {
    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">{editingId ? 'Edit Employee' : 'Add Employee'}</h1>
          <Button onClick={() => setMode('list')} className="bg-gray-200 text-gray-700 hover:bg-gray-300">
            Back to List
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow">
          <div className="flex gap-1 border-b overflow-x-auto px-4">
            {TABS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={`px-3 py-3 text-sm font-medium whitespace-nowrap ${
                  tab === t ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          <div className="p-6">
            {tab === 'General' && (
              <GeneralTab
                formData={formData}
                setField={setField}
                lookupLabels={lookupLabels}
                setLookupLabels={setLookupLabels}
                savedEmployeeId={savedEmployeeId}
              />
            )}
            {tab === 'E-PAYROLL Basic 1' && <PayrollBasic1Tab formData={formData} setField={setField} />}
            {tab === 'E-PAYROLL Basic 2' && <PayrollBasic2Tab formData={formData} setField={setField} />}
            {tab === 'E-PAYROLL Additional 1' && (
              <PayrollAdditional1Tab formData={formData} setField={setField} savedEmployeeId={savedEmployeeId} />
            )}
            {tab === 'E-PAYROLL Additional 2' && (
              <PayrollAdditional2Tab formData={formData} setField={setField} savedEmployeeId={savedEmployeeId} />
            )}
            {tab === 'E-TMS' && <TmsTab formData={formData} setField={setField} />}
            {tab === 'E-LEAVE' && <LeaveTab formData={formData} setField={setField} />}
            {tab === 'E-CLAIM' && <ClaimTab formData={formData} setField={setField} />}
            {tab === 'E-APPRAISAL' && <AppraisalTab />}
          </div>

          <div className="px-6 py-4 border-t flex justify-end gap-3">
            <Button type="submit">{editingId ? 'Save Changes' : 'Create Employee'}</Button>
          </div>
        </form>

        {unmappedUsers.length > 0 && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black bg-opacity-40" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6">
              <h3 className="text-lg font-semibold mb-2">Map to System User?</h3>
              <p className="text-sm text-gray-600 mb-4">
                This company has system user account(s) not yet linked to an employee. Link one to the
                new employee record you just created?
              </p>
              <ul className="divide-y mb-4">
                {unmappedUsers.map((u) => (
                  <li key={u.id} className="py-2 flex justify-between items-center text-sm">
                    <span>{u.name || u.email}</span>
                    <button onClick={() => mapUnmappedUser(u.id)} className="text-blue-600 hover:underline">
                      Map
                    </button>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => {
                  setUnmappedUsers([]);
                  setNewEmployeeId(null);
                  setMode('list');
                  fetchEmployees();
                }}
                className="w-full bg-gray-200 text-gray-700 hover:bg-gray-300"
              >
                Skip
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Employees</h1>
        <div className="flex items-center gap-4">
          <label className="text-sm text-gray-600 flex items-center gap-2">
            <input type="checkbox" checked={showResigned} onChange={(e) => setShowResigned(e.target.checked)} />
            Show Resigned
          </label>
          <Button onClick={openCreateForm}>Add Employee</Button>
        </div>
      </div>

      <Table columns={columns} rows={employees} loading={loading} emptyMessage="No employees yet" />

      <ConfirmDialog
        isOpen={!!resignTarget}
        title="Resign Employee"
        message={`Mark ${resignTarget?.full_name} as resigned? This will deactivate their account.`}
        confirmLabel="Resign"
        onConfirm={handleResign}
        onCancel={() => setResignTarget(null)}
      />
    </div>
  );
}

function GeneralTab({
  formData,
  setField,
  lookupLabels,
  setLookupLabels,
  savedEmployeeId,
}: {
  formData: any;
  setField: (k: string, v: any) => void;
  lookupLabels: Record<string, string>;
  setLookupLabels: (v: any) => void;
  savedEmployeeId: string | null;
}) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <Input label="Employee Code" value={formData.code} onChange={(e) => setField('code', e.target.value)} placeholder="Leave blank to auto-generate (if enabled)" />
        <Input label="Email" type="email" value={formData.email} onChange={(e) => setField('email', e.target.value)} required />
        <Input label="Mobile Number" value={formData.mobileNumber} onChange={(e) => setField('mobileNumber', e.target.value)} />
        <Input label="First Name" value={formData.firstName} onChange={(e) => setField('firstName', e.target.value)} required />
        <Input label="Last Name" value={formData.lastName} onChange={(e) => setField('lastName', e.target.value)} />
        <Input label="Date of Birth" type="date" value={formData.dateOfBirth} onChange={(e) => setField('dateOfBirth', e.target.value)} />
        <Select
          label="Gender"
          value={formData.gender}
          onChange={(e) => setField('gender', e.target.value)}
          options={[
            { value: 'Male', label: 'Male' },
            { value: 'Female', label: 'Female' },
            { value: 'Other', label: 'Other' },
          ]}
        />
        <Select
          label="ID Type"
          value={formData.idType}
          onChange={(e) => setField('idType', e.target.value)}
          options={[
            { value: 'NRIC', label: 'NRIC' },
            { value: 'PASSPORT', label: 'Passport' },
            { value: 'WORK_PERMIT', label: 'Work Permit' },
            { value: 'FIN', label: 'FIN' },
          ]}
        />
        <Input label="NRIC" value={formData.nric} onChange={(e) => setField('nric', e.target.value)} />
        <Input label="Passport Number" value={formData.passportNumber} onChange={(e) => setField('passportNumber', e.target.value)} />
        <Input label="Visa/Work Pass Number" value={formData.visaNumber} onChange={(e) => setField('visaNumber', e.target.value)} />
        <Select
          label="Marital Status"
          value={formData.maritalStatus}
          onChange={(e) => setField('maritalStatus', e.target.value)}
          options={[
            { value: 'Single', label: 'Single' },
            { value: 'Married', label: 'Married' },
            { value: 'Divorced', label: 'Divorced' },
            { value: 'Widowed', label: 'Widowed' },
          ]}
        />
        <Input label="Address" value={formData.address} onChange={(e) => setField('address', e.target.value)} />
        <Input label="Country" value={formData.country} onChange={(e) => setField('country', e.target.value)} />
      </div>

      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Business Details</h4>
        <div className="grid grid-cols-3 gap-4">
          <LookupSelect
            label="Department"
            value={formData.departmentId}
            displayValue={lookupLabels.departmentId}
            onChange={(v, opt) => {
              setField('departmentId', v);
              if (opt) setLookupLabels((p: any) => ({ ...p, departmentId: opt.label }));
            }}
            fetchOptions={fetchDepartmentOptions}
          />
          <LookupSelect
            label="Designation"
            value={formData.designationId}
            displayValue={lookupLabels.designationId}
            onChange={(v, opt) => {
              setField('designationId', v);
              if (opt) setLookupLabels((p: any) => ({ ...p, designationId: opt.label }));
            }}
            fetchOptions={fetchDesignationOptions}
          />
          <Input label="Cost Center" value={formData.costCenter} onChange={(e) => setField('costCenter', e.target.value)} />
          <LookupSelect
            label="Manager"
            value={formData.managerId}
            displayValue={lookupLabels.managerId}
            onChange={(v, opt) => {
              setField('managerId', v);
              if (opt) setLookupLabels((p: any) => ({ ...p, managerId: opt.label }));
            }}
            fetchOptions={fetchEmployeeOptions}
          />
        </div>
      </div>

      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Employment</h4>
        <div className="grid grid-cols-3 gap-4">
          <Input label="Start Date" type="date" value={formData.startDate} onChange={(e) => setField('startDate', e.target.value)} required />
          <Input label="Probation End Date" type="date" value={formData.probationEndDate} onChange={(e) => setField('probationEndDate', e.target.value)} />
          <Input label="Confirmation Date" type="date" value={formData.confirmationDate} onChange={(e) => setField('confirmationDate', e.target.value)} />
          <Select
            label="Employment Type"
            value={formData.employmentType}
            onChange={(e) => setField('employmentType', e.target.value)}
            options={[
              { value: 'Permanent', label: 'Permanent' },
              { value: 'Contract', label: 'Contract' },
              { value: 'Temporary', label: 'Temporary' },
              { value: 'Part-time', label: 'Part-time' },
            ]}
          />
          <Select
            label="Citizenship Status"
            value={formData.citizenshipStatus}
            onChange={(e) => setField('citizenshipStatus', e.target.value)}
            options={[
              { value: 'CITIZEN', label: 'Singapore Citizen' },
              { value: 'PR', label: 'Permanent Resident' },
              { value: 'PR_YEAR1', label: 'PR — 1st Year' },
              { value: 'PR_YEAR2', label: 'PR — 2nd Year' },
              { value: 'FOREIGNER', label: 'Foreigner' },
            ]}
          />
          <Input label="PR Start Date" type="date" value={formData.prStartDate} onChange={(e) => setField('prStartDate', e.target.value)} />
        </div>
      </div>

      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Working Hours (overrides company default)</h4>
        <div className="grid grid-cols-3 gap-4">
          <Input label="Working Hours / Year" type="number" value={formData.workingHoursPerYear} onChange={(e) => setField('workingHoursPerYear', e.target.value)} />
          <Input label="Working Days / Week" type="number" value={formData.workingDaysPerWeek} onChange={(e) => setField('workingDaysPerWeek', e.target.value)} />
          <Input label="Working Hours / Day" type="number" value={formData.workingHoursPerDay} onChange={(e) => setField('workingHoursPerDay', e.target.value)} />
        </div>
      </div>

      {savedEmployeeId ? (
        <ChildrenSection employeeId={savedEmployeeId} />
      ) : (
        <p className="text-sm text-gray-400">Save the employee once to add Children Details.</p>
      )}
    </div>
  );
}

function PayrollBasic1Tab({ formData, setField }: { formData: any; setField: (k: string, v: any) => void }) {
  return (
    <div className="grid grid-cols-3 gap-4">
      <Input label="Basic Salary" type="number" step="0.01" value={formData.basicSalary} onChange={(e) => setField('basicSalary', e.target.value)} />
      <label className="flex items-center gap-2 text-sm text-gray-700 mt-6">
        <input type="checkbox" checked={formData.isVip} onChange={(e) => setField('isVip', e.target.checked)} /> VIP
      </label>
      <label className="flex items-center gap-2 text-sm text-gray-700 mt-6">
        <input type="checkbox" checked={formData.otRateCapped2600} onChange={(e) => setField('otRateCapped2600', e.target.checked)} /> OT rate capped at $2,600
      </label>
      <Select
        label="Pay Type"
        value={formData.payType}
        onChange={(e) => setField('payType', e.target.value)}
        options={[
          { value: 'ONCE_A_MONTH', label: 'Once a Month' },
          { value: 'TWICE_A_MONTH', label: 'Twice a Month' },
        ]}
      />
      {formData.payType === 'TWICE_A_MONTH' && (
        <>
          <Select
            label="Mid-Month Type"
            value={formData.midMonthType}
            onChange={(e) => setField('midMonthType', e.target.value)}
            options={[
              { value: 'PERCENTAGE', label: 'Percentage' },
              { value: 'FIXED_AMOUNT', label: 'Fixed Amount' },
            ]}
          />
          {formData.midMonthType === 'PERCENTAGE' ? (
            <Input label="Mid-Month %" type="number" step="0.01" value={formData.midMonthPercentage} onChange={(e) => setField('midMonthPercentage', e.target.value)} />
          ) : (
            <Input label="Mid-Month Fixed Amount" type="number" step="0.01" value={formData.midMonthFixedAmount} onChange={(e) => setField('midMonthFixedAmount', e.target.value)} />
          )}
        </>
      )}
      <Select
        label="CPF Contribution Type"
        value={formData.cpfContributionType}
        onChange={(e) => setField('cpfContributionType', e.target.value)}
        options={[
          { value: 'FULL', label: 'Full' },
          { value: 'GRADUATED', label: 'Graduated' },
          { value: 'NONE', label: 'None' },
        ]}
      />
      <Select
        label="FWL Contribution Type"
        value={formData.fwlContributionType}
        onChange={(e) => setField('fwlContributionType', e.target.value)}
        options={[
          { value: 'WORK_PERMIT', label: 'Work Permit' },
          { value: 'S_PASS', label: 'S Pass' },
          { value: 'NONE', label: 'None' },
        ]}
      />
      <Input label="PR Approval Date" type="date" value={formData.prApprovalDate} onChange={(e) => setField('prApprovalDate', e.target.value)} />
      <Select
        label="CPF Option (PR 1st/2nd Year)"
        value={formData.cpfOption}
        onChange={(e) => setField('cpfOption', e.target.value)}
        options={[
          { value: 'GRADUATED', label: 'Graduated' },
          { value: 'FULL', label: 'Full' },
        ]}
      />
      <Input
        label="Pay Day Group ID"
        value={formData.payDayGroupId}
        onChange={(e) => setField('payDayGroupId', e.target.value)}
        placeholder="Pay Day Group master screen ships in Phase 4"
      />
    </div>
  );
}

function PayrollBasic2Tab({ formData, setField }: { formData: any; setField: (k: string, v: any) => void }) {
  return (
    <div className="grid grid-cols-3 gap-4">
      <Input label="Employer CPF Ref No." value={formData.employerCpfRefNo} onChange={(e) => setField('employerCpfRefNo', e.target.value)} />
      <Select
        label="Salary Pay Mode"
        value={formData.salaryPayMode}
        onChange={(e) => setField('salaryPayMode', e.target.value)}
        options={[
          { value: 'BANK', label: 'Bank' },
          { value: 'CHEQUE', label: 'Cheque' },
          { value: 'CASH', label: 'Cash' },
        ]}
      />
      {formData.salaryPayMode === 'BANK' && (
        <>
          <Input label="Bank ID" value={formData.bankId} onChange={(e) => setField('bankId', e.target.value)} placeholder="Bank master screen ships in Phase 3" />
          <Input label="Bank Account No." value={formData.bankAccountNo} onChange={(e) => setField('bankAccountNo', e.target.value)} />
        </>
      )}
    </div>
  );
}

function PayrollAdditional1Tab({
  formData,
  setField,
  savedEmployeeId,
}: {
  formData: any;
  setField: (k: string, v: any) => void;
  savedEmployeeId: string | null;
}) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <Input label="EP/Work Permit Number" value={formData.epWpNumber} onChange={(e) => setField('epWpNumber', e.target.value)} />
        <Input label="EP/Work Permit Expiry Date" type="date" value={formData.epWpExpiryDate} onChange={(e) => setField('epWpExpiryDate', e.target.value)} />
      </div>
      {savedEmployeeId ? (
        <>
          <MonthlyAllowancesSection employeeId={savedEmployeeId} />
          <PeriodAllowancesSection employeeId={savedEmployeeId} />
        </>
      ) : (
        <p className="text-sm text-gray-400">Save the employee once to add recurring/period allowances.</p>
      )}
    </div>
  );
}

function PayrollAdditional2Tab({
  formData,
  setField,
  savedEmployeeId,
}: {
  formData: any;
  setField: (k: string, v: any) => void;
  savedEmployeeId: string | null;
}) {
  const toggleFund = (code: string) => {
    const current: string[] = formData.fundOptOuts || [];
    setField('fundOptOuts', current.includes(code) ? current.filter((c) => c !== code) : [...current, code]);
  };
  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Opt out of Fund Contribution</h4>
        <div className="grid grid-cols-2 gap-2">
          {FUND_CODES.map((f) => (
            <label key={f.code} className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={(formData.fundOptOuts || []).includes(f.code)} onChange={() => toggleFund(f.code)} />
              {f.label}
            </label>
          ))}
        </div>
      </div>
      {savedEmployeeId ? (
        <RemindersSection employeeId={savedEmployeeId} />
      ) : (
        <p className="text-sm text-gray-400">Save the employee once to add custom reminders.</p>
      )}
    </div>
  );
}

function TmsTab({ formData, setField }: { formData: any; setField: (k: string, v: any) => void }) {
  return (
    <div className="grid grid-cols-3 gap-4">
      <Select
        label="OT Type"
        value={formData.otType}
        onChange={(e) => setField('otType', e.target.value)}
        options={[
          { value: 'NORMAL_RATE', label: 'Normal Rate' },
          { value: 'FLAT_RATE', label: 'Flat Rate' },
          { value: 'HOURLY_RATE', label: 'Hourly Rate' },
          { value: 'DAILY_RATE', label: 'Daily Rate' },
          { value: 'VIP', label: 'VIP' },
        ]}
      />
      {formData.otType === 'FLAT_RATE' && (
        <Input label="OT Flat Rate Amount" type="number" step="0.01" value={formData.otFlatRateAmount} onChange={(e) => setField('otFlatRateAmount', e.target.value)} />
      )}
      <LookupSelect label="Main Branch" value={formData.mainBranchId} onChange={(v) => setField('mainBranchId', v)} fetchOptions={fetchBranchOptions} />
      <LookupSelect label="Default Shift" value={formData.defaultShiftId} onChange={(v) => setField('defaultShiftId', v)} fetchOptions={fetchShiftOptions} />
      <Select
        label="Shift Assignment Method"
        value={formData.shiftAssignmentMethod}
        onChange={(e) => setField('shiftAssignmentMethod', e.target.value)}
        options={[
          { value: 'DUTY_ROSTER', label: 'Duty Roster' },
          { value: 'AUTO_SHIFT', label: 'Auto Shift' },
          { value: 'SCHEDULE', label: 'Schedule' },
        ]}
      />
      {formData.shiftAssignmentMethod === 'DUTY_ROSTER' && (
        <>
          <LookupSelect label="Duty Roster" value={formData.dutyRosterId} onChange={(v) => setField('dutyRosterId', v)} fetchOptions={fetchDutyRosterOptions} />
          <Input label="Duty Roster Group" value={formData.dutyRosterGroup} onChange={(e) => setField('dutyRosterGroup', e.target.value)} />
        </>
      )}
      {formData.shiftAssignmentMethod === 'AUTO_SHIFT' && (
        <Input label="Auto Shift ID" value={formData.autoShiftId} onChange={(e) => setField('autoShiftId', e.target.value)} placeholder="Auto Shift master screen ships in Phase 3" />
      )}
    </div>
  );
}

function LeaveTab({ formData, setField }: { formData: any; setField: (k: string, v: any) => void }) {
  return (
    <div className="grid grid-cols-3 gap-4">
      <LookupSelect label="Leave Category" value={formData.leaveCategoryId} onChange={(v) => setField('leaveCategoryId', v)} fetchOptions={fetchLeaveCategoryOptions} />
      <LookupSelect label="Exclude Days Group" value={formData.excludeDaysGroupId} onChange={(v) => setField('excludeDaysGroupId', v)} fetchOptions={fetchExcludeDaysOptions} />
      <LookupSelect label="Replacement Staff" value={formData.replacementStaffId} onChange={(v) => setField('replacementStaffId', v)} fetchOptions={fetchEmployeeOptions} />
      <p className="col-span-3 text-sm text-gray-500">
        1st/2nd/Final leave approvers are assigned on the Approver Settings page, not here.
      </p>
    </div>
  );
}

function ClaimTab({ formData, setField }: { formData: any; setField: (k: string, v: any) => void }) {
  return (
    <div className="grid grid-cols-3 gap-4">
      <Input
        label="Claim Date Range ID"
        value={formData.claimDateRangeId}
        onChange={(e) => setField('claimDateRangeId', e.target.value)}
        placeholder="Claim Date Range master screen ships in Phase 3"
      />
      <Input
        label="Claim Limit ID"
        value={formData.claimLimitId}
        onChange={(e) => setField('claimLimitId', e.target.value)}
        placeholder="Claim Limit master screen ships in Phase 3"
      />
    </div>
  );
}

function AppraisalTab() {
  return (
    <p className="text-sm text-gray-500">
      Appraisal cycles are assigned by template + month on the Appraisal module's Assign Appraisal
      screen, not per-employee here.
    </p>
  );
}

function ChildrenSection({ employeeId }: { employeeId: string }) {
  const [children, setChildren] = useState<any[]>([]);
  const [form, setForm] = useState({ childName: '', dateOfBirth: '', gender: '', birthCertNo: '', isSgCitizen: false });

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  const load = async () => {
    const res = await api.get(`/employees/${employeeId}/children`);
    setChildren(res.data.children || []);
  };

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post(`/employees/${employeeId}/children`, form);
      setForm({ childName: '', dateOfBirth: '', gender: '', birthCertNo: '', isSgCitizen: false });
      load();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to add child');
    }
  };

  const remove = async (id: string) => {
    await api.delete(`/employees/${employeeId}/children/${id}`);
    load();
  };

  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-700 mb-2">Children Details</h4>
      <form onSubmit={add} className="grid grid-cols-5 gap-3 mb-3">
        <Input placeholder="Child Name" value={form.childName} onChange={(e) => setForm({ ...form, childName: e.target.value })} required />
        <Input type="date" value={form.dateOfBirth} onChange={(e) => setForm({ ...form, dateOfBirth: e.target.value })} />
        <Select
          value={form.gender}
          onChange={(e) => setForm({ ...form, gender: e.target.value })}
          options={[
            { value: 'Male', label: 'Male' },
            { value: 'Female', label: 'Female' },
          ]}
        />
        <Input placeholder="Birth Cert No." value={form.birthCertNo} onChange={(e) => setForm({ ...form, birthCertNo: e.target.value })} />
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 text-sm">
            <input type="checkbox" checked={form.isSgCitizen} onChange={(e) => setForm({ ...form, isSgCitizen: e.target.checked })} /> SG Citizen
          </label>
          <Button type="submit" className="ml-auto">Add</Button>
        </div>
      </form>
      <ul className="divide-y">
        {children.map((c) => (
          <li key={c.id} className="py-2 flex justify-between items-center text-sm">
            <span>
              {c.child_name} — {c.gender || '—'} {c.is_sg_citizen ? '(SG Citizen)' : ''}
            </span>
            <button onClick={() => remove(c.id)} className="text-red-600 hover:underline">Remove</button>
          </li>
        ))}
        {children.length === 0 && <p className="text-gray-400 text-sm py-2">No children on record</p>}
      </ul>
    </div>
  );
}

function MonthlyAllowancesSection({ employeeId }: { employeeId: string }) {
  const [rows, setRows] = useState<any[]>([]);
  const [form, setForm] = useState({ code: '', name: '', amount: '', isDeduction: false });

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  const load = async () => {
    const res = await api.get(`/employees/${employeeId}/monthly-allowances`);
    setRows(res.data.allowances || []);
  };

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post(`/employees/${employeeId}/monthly-allowances`, form);
      setForm({ code: '', name: '', amount: '', isDeduction: false });
      load();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to add');
    }
  };

  const remove = async (id: string) => {
    await api.delete(`/employees/${employeeId}/monthly-allowances/${id}`);
    load();
  };

  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-700 mb-2">Monthly Recurring Allowances/Deductions</h4>
      <form onSubmit={add} className="grid grid-cols-5 gap-3 mb-3">
        <Input placeholder="Code" maxLength={6} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required />
        <Input placeholder="Name" maxLength={50} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        <Input type="number" step="0.01" placeholder="Amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required />
        <label className="flex items-center gap-1 text-sm">
          <input type="checkbox" checked={form.isDeduction} onChange={(e) => setForm({ ...form, isDeduction: e.target.checked })} /> Deduction
        </label>
        <Button type="submit">Add</Button>
      </form>
      <ul className="divide-y">
        {rows.map((r) => (
          <li key={r.id} className="py-2 flex justify-between items-center text-sm">
            <span>
              {r.code} — {r.name}: {r.is_deduction ? '-' : ''}
              {r.amount}
            </span>
            <button onClick={() => remove(r.id)} className="text-red-600 hover:underline">Remove</button>
          </li>
        ))}
        {rows.length === 0 && <p className="text-gray-400 text-sm py-2">None</p>}
      </ul>
    </div>
  );
}

function PeriodAllowancesSection({ employeeId }: { employeeId: string }) {
  const [rows, setRows] = useState<any[]>([]);
  const [form, setForm] = useState({ code: '', name: '', amount: '', isDeduction: false, startDate: '', endDate: '' });

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  const load = async () => {
    const res = await api.get(`/employees/${employeeId}/period-allowances`);
    setRows(res.data.allowances || []);
  };

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post(`/employees/${employeeId}/period-allowances`, form);
      setForm({ code: '', name: '', amount: '', isDeduction: false, startDate: '', endDate: '' });
      load();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to add');
    }
  };

  const remove = async (id: string) => {
    await api.delete(`/employees/${employeeId}/period-allowances/${id}`);
    load();
  };

  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-700 mb-2">Specify-Period Allowance</h4>
      <form onSubmit={add} className="grid grid-cols-6 gap-3 mb-3">
        <Input placeholder="Code" maxLength={6} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required />
        <Input placeholder="Name" maxLength={50} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        <Input type="number" step="0.01" placeholder="Amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required />
        <Input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} required />
        <Input type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} required />
        <Button type="submit">Add</Button>
      </form>
      <ul className="divide-y">
        {rows.map((r) => (
          <li key={r.id} className="py-2 flex justify-between items-center text-sm">
            <span>
              {r.code} — {r.name}: {r.is_deduction ? '-' : ''}
              {r.amount} ({new Date(r.start_date).toLocaleDateString()} – {new Date(r.end_date).toLocaleDateString()})
            </span>
            <button onClick={() => remove(r.id)} className="text-red-600 hover:underline">Remove</button>
          </li>
        ))}
        {rows.length === 0 && <p className="text-gray-400 text-sm py-2">None</p>}
      </ul>
    </div>
  );
}

function RemindersSection({ employeeId }: { employeeId: string }) {
  const [rows, setRows] = useState<any[]>([]);
  const [form, setForm] = useState({ title: '', remindDate: '', daysBeforeExpiry: '30', notes: '' });

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  const load = async () => {
    const res = await api.get(`/employees/${employeeId}/reminders`);
    setRows(res.data.reminders || []);
  };

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post(`/employees/${employeeId}/reminders`, form);
      setForm({ title: '', remindDate: '', daysBeforeExpiry: '30', notes: '' });
      load();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to add');
    }
  };

  const remove = async (id: string) => {
    await api.delete(`/employees/${employeeId}/reminders/${id}`);
    load();
  };

  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-700 mb-2">Custom Other Reminders</h4>
      <form onSubmit={add} className="grid grid-cols-4 gap-3 mb-3">
        <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
        <Input type="date" value={form.remindDate} onChange={(e) => setForm({ ...form, remindDate: e.target.value })} required />
        <Input type="number" placeholder="Days Before" value={form.daysBeforeExpiry} onChange={(e) => setForm({ ...form, daysBeforeExpiry: e.target.value })} />
        <Button type="submit">Add</Button>
      </form>
      <ul className="divide-y">
        {rows.map((r) => (
          <li key={r.id} className="py-2 flex justify-between items-center text-sm">
            <span>
              {r.title} — {new Date(r.remind_date).toLocaleDateString()} ({r.days_before_expiry}d before)
            </span>
            <button onClick={() => remove(r.id)} className="text-red-600 hover:underline">Remove</button>
          </li>
        ))}
        {rows.length === 0 && <p className="text-gray-400 text-sm py-2">None</p>}
      </ul>
    </div>
  );
}
